import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * Inspection messaging panel — chat-like thread visible to all participants
 * (contractor + workflow assignees + AAYDA + superadmin).
 */
export default function InspectionChat({ inspectionId }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/inspections/${inspectionId}/messages`);
      setMessages(r.data || []);
    } catch (e) {
      // 403 means user is not allowed — hide silently
      if (e?.response?.status !== 403) {
        toast.error(t("error") || "Xəta");
      }
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/inspections/${inspectionId}/messages`, { body });
      setBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6"
      data-testid="inspection-chat"
    >
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#004D99]" />
        <h3 className="font-heading font-bold text-lg">Mesajlaşma</h3>
        <span className="mono text-[10px] text-slate-400 ml-1">
          {messages.length}
        </span>
      </div>
      <div
        ref={scrollerRef}
        className="max-h-80 overflow-y-auto space-y-3 pr-1 mb-3 scroll-thin"
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            Hələ mesaj yoxdur. Söhbəti başladın.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.author_id === user?.id;
          return (
            <div
              key={m.id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`chat-msg-${m.id}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  mine
                    ? "bg-[#004D99] text-white"
                    : "bg-slate-100 text-slate-900"
                }`}
              >
                {!mine && (
                  <div className="text-[10px] mono uppercase tracking-wide opacity-70 mb-0.5">
                    {m.author_name} · {t(`role_label.${m.author_role}`)}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                <div
                  className={`text-[10px] mono mt-1 ${mine ? "text-blue-100" : "text-slate-400"}`}
                >
                  {new Date(m.created_at).toLocaleString("az")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 items-end">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Mesajınızı yazın... (Ctrl+Enter ilə göndər)"
          rows={2}
          className="flex-1 resize-none"
          data-testid="chat-input"
        />
        <Button
          onClick={send}
          disabled={sending || !body.trim()}
          className="bg-[#004D99] hover:bg-[#003366] h-full"
          data-testid="chat-send"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
